/**
 * Pure helpers for the folder hierarchy (`folders.parentId`, null = root).
 * Matches mobile, which lists root folders with `parentId == null`.
 */

export const MAX_FOLDER_DEPTH = 10;

const parentOf = (f) => f?.parentId || null;

export function childFolders(folders, parentId = null) {
  return (folders || []).filter((f) => parentOf(f) === (parentId || null));
}

/** Root-first chain of folders ending at `folderId`; stops on broken links or cycles. */
export function folderPath(folders, folderId) {
  const byId = new Map((folders || []).map((f) => [f.id, f]));
  const path = [];
  const seen = new Set();
  let cur = byId.get(folderId);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur);
    cur = byId.get(parentOf(cur));
  }
  return path;
}

export function descendantIds(folders, folderId) {
  const out = new Set();
  const stack = [folderId];
  while (stack.length) {
    const id = stack.pop();
    for (const child of childFolders(folders, id)) {
      if (!out.has(child.id) && child.id !== folderId) {
        out.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return out;
}

function subtreeHeight(folders, folderId, seen = new Set()) {
  if (seen.has(folderId)) return 0;
  seen.add(folderId);
  const kids = childFolders(folders, folderId);
  return 1 + (kids.length ? Math.max(...kids.map((k) => subtreeHeight(folders, k.id, seen))) : 0);
}

/** Returns null when allowed, otherwise an error code. */
export function moveFolderError(folders, folderId, targetParentId) {
  const target = targetParentId || null;
  if (!folderId) return "FOLDER_NOT_FOUND";
  if (target === folderId) return "FOLDER_MOVE_INTO_SELF";
  if (target && descendantIds(folders, folderId).has(target)) return "FOLDER_MOVE_INTO_CHILD";
  if (target && !(folders || []).some((f) => f.id === target)) return "FOLDER_NOT_FOUND";
  const depth = (target ? folderPath(folders, target).length : 0) + subtreeHeight(folders, folderId);
  if (depth > MAX_FOLDER_DEPTH) return "FOLDER_TOO_DEEP";
  return null;
}

/** Folder choices for a move dialog: root + every folder, indented, minus invalid targets. */
export function moveTargets(folders, movingFolderId = null) {
  const blocked = movingFolderId ? new Set([movingFolderId, ...descendantIds(folders, movingFolderId)]) : new Set();
  const out = [];
  const walk = (parentId, depth, seen) => {
    for (const f of childFolders(folders, parentId).sort((a, b) => String(a.name).localeCompare(String(b.name)))) {
      if (seen.has(f.id)) continue;
      seen.add(f.id);
      if (!blocked.has(f.id)) {
        out.push({ id: f.id, name: f.name, depth });
        walk(f.id, depth + 1, seen);
      }
    }
  };
  walk(null, 0, new Set());
  return out;
}
